import { createAsyncThunk } from '@reduxjs/toolkit';
import { SelectOption } from 'src/types/SelectOption';
import { errorLoaded } from '../slices/errorSlice';
import {
  isValidCode,
  thunkExecuteAndReturnResultOrShowError,
} from '../../services/base.service';
import {
  deleteLiteracyScreeningFile,
  getLiteracyScreeningContact,
  getLiteracyScreeningDataCollectionChecks,
  getLiteracyScreeningDiagnostics,
  getLiteracyScreeningFilesByLea,
  getLiteracyScreeningFilesBySchool,
  getLiteracyScreeningLEASchools,
  getLiteracyScreeningOptionSets,
  getLiteracyScreeningSchoolYears,
  postLiteracyScreening,
  postLiteracyScreeningContact,
  postLiteracyScreeningDiagnostics,
  putLiteracyScreening,
  putLiteracyScreeningContact,
  putLiteracyScreeningDiagnostics,
  putLiteracyScreeningDocument,
  uploadLiteracyScreeningFile,
} from '../../services/literacyScreening.service';
import { LiteracyScreeningContactModel } from '../../types/LiteracyScreeningContactModel';
import { ApiResponseModel } from '../../types/ApiResponseModel';
import { LiteracyScreeningDiagnosticMeasuresModel } from '../../types/LiteracyScreeningDiagnosticMeasuresModel';
import { LiteracyScreeningFileListItemModel } from '../../types/LiteracyScreeningFileListItemModel';
import { LiteracyScreeningLEASchoolModel } from '../../types/LiteracyScreeningLEASchoolModel';
import { LiteracyScreeningModel } from '../../types/LiteracyScreeningModel';
import { OptionSetsPageModel } from '../../types/OptionSetsPageModel';
import { ThunkAsyncBackbone } from '../../types/ThunkAsyncBackbone';

const executeScreeningSave = async <T>(
  thunkAPI: ThunkAsyncBackbone,
  request: Promise<ApiResponseModel<T>>
): Promise<T> => {
  const response = await request;
  if (!isValidCode(response.status)) {
    if (!response.aborted) {
      thunkAPI.dispatch(errorLoaded(response.error));
    }
    throw new Error(response.error?.title || 'API request failed');
  }
  return response.data;
};

const emptyContact = (): LiteracyScreeningContactModel => ({
  id: '',
  leaIdentifier: '',
  firstName: '',
  lastOrSurname: '',
  position: '',
  email: '',
  telephone: '',
});

export const fetchLiteracyScreeningContact = createAsyncThunk(
  'literacyScreening/fetchLiteracyScreeningContact',
  async (
    options: { showLoading?: boolean } = { showLoading: true },
    thunkAPI
  ) => {
    try {
      const response = await getLiteracyScreeningContact({
        signal: thunkAPI.signal as AbortSignal,
      });

      if (isValidCode(response.status)) {
        return { data: response.data, showLoading: options.showLoading };
      }

      return {
        data: emptyContact(),
        showLoading: options.showLoading,
      };
    } catch (error) {
      console.warn(
        'Failed to fetch literacy screening contact, using default values:',
        error
      );
      return {
        data: emptyContact(),
        showLoading: options.showLoading,
      };
    }
  }
);

export const fetchLiteracyScreeningSchoolYears = createAsyncThunk(
  'literacyScreening/fetchLiteracyScreeningSchoolYears',
  async (_, thunkAPI) => {
    return await thunkExecuteAndReturnResultOrShowError<SelectOption[]>(
      thunkAPI,
      getLiteracyScreeningSchoolYears({ signal: thunkAPI.signal })
    );
  }
);

export const fetchLiteracyScreeningLEASchools = createAsyncThunk(
  'literacyScreening/fetchLiteracyScreeningLEASchools',
  async (schoolYear: string, thunkAPI) => {
    return await thunkExecuteAndReturnResultOrShowError<LiteracyScreeningLEASchoolModel>(
      thunkAPI,
      getLiteracyScreeningLEASchools(schoolYear, {
        signal: thunkAPI.signal,
      })
    );
  }
);

export const fetchLiteracyScreeningUpdate = createAsyncThunk(
  'literacyScreening/fetchLiteracyScreeningUpdate',
  async (schoolYear: string, thunkAPI) => {
    return await thunkExecuteAndReturnResultOrShowError<boolean>(
      thunkAPI,
      getLiteracyScreeningDataCollectionChecks(schoolYear, {
        signal: thunkAPI.signal,
      })
    );
  }
);

export const fetchLiteracyScreeningOptionSets = createAsyncThunk(
  'literacyScreening/fetchLiteracyScreeningOptionSets',
  async (
    {
      module,
      page,
      include,
    }: { module: string; page: string; include?: string },
    thunkAPI
  ) => {
    return await thunkExecuteAndReturnResultOrShowError<OptionSetsPageModel>(
      thunkAPI,
      getLiteracyScreeningOptionSets(
        module,
        page,
        {
          signal: thunkAPI.signal,
        },
        include
      )
    );
  }
);

export const fetchLiteracyScreeningDiagnostics = createAsyncThunk(
  'literacyScreening/fetchLiteracyScreeningDiagnostics',
  async (params: { schoolYear: string; showLoading?: boolean }, thunkAPI) => {
    return await thunkExecuteAndReturnResultOrShowError<
      LiteracyScreeningDiagnosticMeasuresModel[]
    >(
      thunkAPI,
      getLiteracyScreeningDiagnostics(params.schoolYear, {
        signal: thunkAPI.signal,
      })
    );
  }
);

export const createLiteracyScreeningDiagnostics = createAsyncThunk(
  'literacyScreening/createLiteracyScreeningDiagnostics',
  async (
    model: LiteracyScreeningDiagnosticMeasuresModel,
    thunkAPI
  ): Promise<string> => {
    return await thunkExecuteAndReturnResultOrShowError<string>(
      thunkAPI,
      postLiteracyScreeningDiagnostics(model, {
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);

export const updateLiteracyScreeningDiagnostics = createAsyncThunk(
  'literacyScreening/updateLiteracyScreeningDiagnostics',
  async (
    params: { id: string; model: LiteracyScreeningDiagnosticMeasuresModel },
    thunkAPI
  ): Promise<void> => {
    return await thunkExecuteAndReturnResultOrShowError<void>(
      thunkAPI,
      putLiteracyScreeningDiagnostics(params.id, params.model, {
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);

export const createLiteracyScreeningContact = createAsyncThunk(
  'literacyScreening/createLiteracyScreeningContact',
  async (contact: LiteracyScreeningContactModel, thunkAPI): Promise<string> => {
    return await thunkExecuteAndReturnResultOrShowError<string>(
      thunkAPI,
      postLiteracyScreeningContact(contact, {
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);

export const updateLiteracyScreeningContact = createAsyncThunk(
  'literacyScreening/updateLiteracyScreeningContact',
  async (contact: LiteracyScreeningContactModel, thunkAPI): Promise<void> => {
    return await thunkExecuteAndReturnResultOrShowError<void>(
      thunkAPI,
      putLiteracyScreeningContact(contact, {
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);

export const createLiteracyScreening = createAsyncThunk(
  'literacyScreening/createLiteracyScreening',
  async (model: LiteracyScreeningModel, thunkAPI): Promise<string> => {
    return await executeScreeningSave<string>(
      thunkAPI,
      postLiteracyScreening(model, {
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);

export const updateLiteracyScreening = createAsyncThunk(
  'literacyScreening/updateLiteracyScreening',
  async (
    params: { id: string; model: LiteracyScreeningModel },
    thunkAPI
  ): Promise<void> => {
    return await executeScreeningSave<void>(
      thunkAPI,
      putLiteracyScreening(params.id, params.model, {
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);

export const uploadLiteracyScreeningFileThunk = createAsyncThunk(
  'literacyScreening/uploadFile',
  async (
    params: {
      schoolYear: string;
      schoolIdentifier: string;
      leaIdentifier: string;
      file: File;
      documentType: string;
    },
    thunkAPI
  ): Promise<{ id: string }> => {
    const response = await uploadLiteracyScreeningFile(
      params.schoolYear,
      params.schoolIdentifier,
      params.leaIdentifier,
      params.file,
      params.documentType,
      {
        signal: thunkAPI.signal as AbortSignal,
      }
    );

    if (!isValidCode(response.status)) {
      const errorMessage =
        response.error?.detail ||
        response.error?.title ||
        'Failed to upload file. Please try again.';
      throw new Error(errorMessage);
    }

    return response.data;
  }
);

export const deleteLiteracyScreeningFileThunk = createAsyncThunk(
  'literacyScreening/deleteFile',
  async (params: { leaId: string; id: string }, thunkAPI): Promise<void> => {
    return await thunkExecuteAndReturnResultOrShowError<void>(
      thunkAPI,
      deleteLiteracyScreeningFile(params.leaId, params.id, {
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);

const filesForSchoolYear = (
  files: LiteracyScreeningFileListItemModel[] | undefined,
  schoolYear: string
): LiteracyScreeningFileListItemModel[] =>
  (files || []).filter(
    (file) => String(file.schoolYear || '') === String(schoolYear)
  );

export const fetchLiteracyScreeningFilesByLeaThunk = createAsyncThunk(
  'literacyScreening/fetchFilesByLea',
  async (
    params: { leaId: string; schoolYear: string; showLoading?: boolean },
    thunkAPI
  ): Promise<LiteracyScreeningFileListItemModel[]> => {
    const files = await thunkExecuteAndReturnResultOrShowError<
      LiteracyScreeningFileListItemModel[]
    >(
      thunkAPI,
      getLiteracyScreeningFilesByLea(params.leaId, {
        signal: thunkAPI.signal as AbortSignal,
      })
    );

    return filesForSchoolYear(files, params.schoolYear);
  }
);

export const fetchLiteracyScreeningFilesBySchoolThunk = createAsyncThunk(
  'literacyScreening/fetchFilesBySchool',
  async (
    params: {
      schoolYear: string;
      leaId: string;
      schoolIdentifier: string;
      showLoading?: boolean;
    },
    thunkAPI
  ): Promise<LiteracyScreeningFileListItemModel[]> => {
    return await thunkExecuteAndReturnResultOrShowError<
      LiteracyScreeningFileListItemModel[]
    >(
      thunkAPI,
      getLiteracyScreeningFilesBySchool(
        params.schoolYear,
        params.leaId,
        params.schoolIdentifier,
        {
          signal: thunkAPI.signal as AbortSignal,
        }
      )
    );
  }
);

export const updateLiteracyScreeningDocumentThunk = createAsyncThunk(
  'literacyScreening/updateDocument',
  async (
    params: { id: string; model: LiteracyScreeningFileListItemModel },
    thunkAPI
  ): Promise<void> => {
    return await thunkExecuteAndReturnResultOrShowError<void>(
      thunkAPI,
      putLiteracyScreeningDocument(params.id, params.model, {
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);
